import { FC, useMemo } from "react";

import MuiAvatar, { AvatarProps as MuiAvatarProps } from "@mui/material/Avatar";
import { useTheme, Theme, getContrastRatio } from "@mui/material/styles";

import { mergeSx } from "@/common/helpers/mergeSx";
import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon";
import { User } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export type DashboardAvatarProps = Pick<MuiAvatarProps, "alt" | "src" | "sx"> & {
  fallbackBackgroundColor?: string | ((theme: Theme) => string);
  fallbackColor?: string | ((theme: Theme) => string);
  size?: "extraSmall" | "small" | "medium" | "large";
  fallbackInitials?: string;
  fallbackIcon?: IconElement;
  slotProps?: {
    fallbackIcon?: Omit<DashboardIconProps, "children" | "ref">;
  };
};

export const DashboardAvatar: FC<DashboardAvatarProps> = ({
  fallbackBackgroundColor,
  fallbackColor,
  size = "medium",
  fallbackIcon = <User />,
  fallbackInitials,
  slotProps,
  sx,
  ...rest
}) => {
  const theme = useTheme();

  const darkTextColor = theme.palette.text.neutral.intense;
  const lightTextColor = theme.palette.text.reverse.moderate;

  const { width, height, typography, fallbackIconSize } = useMemo(() => {
    switch (size) {
      case "extraSmall":
        return {
          width: theme.avatar.size.extraSmall,
          height: theme.avatar.size.extraSmall,
          typography: {
            ...theme.typography.caption,
            fontSize: "10px",
          },
          fallbackIconSize: "extraSmall" as DashboardIconProps["size"],
        };
      case "small":
        return {
          width: theme.avatar.size.small,
          height: theme.avatar.size.small,
          typography: theme.typography.caption,
          fallbackIconSize: "extraSmall" as DashboardIconProps["size"],
        };
      case "medium":
        return {
          width: theme.avatar.size.medium,
          height: theme.avatar.size.medium,
          typography: theme.typography.body2,
          fallbackIconSize: "small" as DashboardIconProps["size"],
        };
      case "large":
        return {
          width: theme.avatar.size.large,
          height: theme.avatar.size.large,
          typography: theme.typography.body1,
          fallbackIconSize: "medium" as DashboardIconProps["size"],
        };
    }
  }, [theme, size]);

  const backgroundColor = useMemo(() => {
    if (typeof fallbackBackgroundColor === "function") {
      return fallbackBackgroundColor(theme);
    }
    return fallbackBackgroundColor ?? theme.palette.surface.neutral.quiet;
  }, [fallbackBackgroundColor, theme]);

  const color = useMemo(() => {
    if (typeof fallbackColor === "function") {
      return fallbackColor(theme);
    }

    const darkTextContrastRatio = getContrastRatio(backgroundColor, darkTextColor);
    const lightTextContrastRatio = getContrastRatio(backgroundColor, lightTextColor);

    return fallbackColor ?? (darkTextContrastRatio > lightTextContrastRatio ? darkTextColor : lightTextColor);
  }, [fallbackColor, theme, backgroundColor, darkTextColor, lightTextColor]);

  const styles = mergeSx(
    {
      backgroundColor,
      color,
      width,
      height,
      typography,
      textTransform: "uppercase",
    },
    sx
  );

  return (
    <MuiAvatar {...rest} sx={styles}>
      {fallbackInitials || (
        <DashboardIcon vibe="currentColor" size={fallbackIconSize} {...slotProps?.fallbackIcon}>
          {fallbackIcon}
        </DashboardIcon>
      )}
    </MuiAvatar>
  );
};
